import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { collection, addDoc, serverTimestamp, getDocs, query, where } from "firebase/firestore";
import { useAuth } from "../utils/useAuth";
import { useNavigate } from "react-router-dom";
import { createManagerNotification } from "../utils/notifications";

export default function AddGame() {
  const { user, profile, effectiveRole } = useAuth();
  const nav = useNavigate();

  const [form, setForm] = useState({
    teamA: "",
    teamAGrade: "",
    teamB: "",
    teamBGrade: "",
    date: "",
    time: "",
    venue: "",
    field: "",
    ageGroup: "",
    competition: "",
    needsReferee: false,
    needsAssistantReferee: false,
    needsAssistant4: false,
    needsAssistant5: false,
    needsTimeKeeper: false,
    needsPlayerMovementRecorder: false,
    needsStatistician: false,
    needsTechnicalZoneManager: false,
    needsPointOfOrigin1: false,
    needsPointOfOrigin2: false,
  });

  const [approvedFields, setApprovedFields] = useState([]);
  const [venues, setVenues] = useState([]);
  const [fieldsForVenue, setFieldsForVenue] = useState([]);
  const [teams, setTeams] = useState([]);
  const [competitions, setCompetitions] = useState([]);

  // Load approved fields
  useEffect(() => {
    async function loadFields() {
      const q = query(collection(db, "fields"), where("approved", "==", true));
      const snap = await getDocs(q);
      const fields = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setApprovedFields(fields);
      
      // Extract unique venues
      const uniqueVenues = [...new Set(fields.map(f => f.venue))];
      setVenues(uniqueVenues);
    }
    loadFields();
  }, []);

  // Load teams/schools/clubs
  useEffect(() => {
    async function loadTeams() {
      const snap = await getDocs(collection(db, 'teams'));
      setTeams(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }
    loadTeams();
  }, []);

  // Load competitions
  useEffect(() => {
    async function loadCompetitions() {
      const snap = await getDocs(collection(db, 'competitions'));
      const comps = snap.docs.map(d => d.data().name);
      comps.sort();
      setCompetitions(comps);
    }
    loadCompetitions();
  }, []);

  // Update available fields when venue changes
  useEffect(() => {
    if (form.venue) {
      const fields = approvedFields
        .filter(f => f.venue === form.venue)
        .map(f => f.fieldName);
      setFieldsForVenue(fields);
      // Reset field selection if current field is not available for new venue
      if (!fields.includes(form.field)) {
        updateField("field", "");
      }
    } else {
      setFieldsForVenue([]);
    }
  }, [form.venue, approvedFields]);

  function updateField(field, value) {
    setForm({ ...form, [field]: value });
  }

  async function submit(e) {
    e.preventDefault();

    // Validate 72-hour minimum notice ONLY for coaches (managers can bypass this)
    if (effectiveRole === 'coach') {
      if (!form.date || !form.time) {
        alert("Please select both date and time for the game.");
        return;
      }

      const gameDateTime = new Date(`${form.date}T${form.time}`);
      const now = new Date();
      const hoursUntilGame = (gameDateTime - now) / (1000 * 60 * 60);

      console.log("Hours until game:", hoursUntilGame);
      console.log("Profile union:", profile.union);

      if (hoursUntilGame < 72) {
        console.log("Game is within 72 hours - sending notification");
        
        // Send notification to managers
        await createManagerNotification(
          "Urgent Game Request",
          `${profile.fullName} attempted to book a game (${form.teamA} vs ${form.teamB}) on ${form.date} at ${form.time}, which is within 72 hours. Please assist with this booking.`,
          profile.union
        );
        
        alert("Please contact your respective union manager to book these games due to time constraint being met");
        return;
      }
    }

    try {
      await addDoc(collection(db, "games"), {
        ...form,
        coachId: user.uid,
        coachName: profile.fullName,
        union: profile.union, // Add union for notification routing
        status: "pending",
        createdAt: serverTimestamp(),
      });

      alert("Game request submitted!");
      nav("/coach");
    } catch (err) {
      alert("Error submitting request: " + err.message);
    }
  }

  return (
    <div className="max-w-xl mx-auto p-6">
      {/* 🔵 Updated Heading */}
      <h2 className="text-2xl font-bold mb-4">Load New Game</h2>

      <form onSubmit={submit} className="space-y-4">

        {/* Team A */}
        <div className="flex gap-2">
          <select
            className="flex-1 p-2 border rounded"
            value={form.teamA}
            onChange={(e) => updateField("teamA", e.target.value)}
            required
          >
            <option value="">Select Team A</option>
            {teams.map(team => (
              <option key={team.id} value={team.name}>{team.name}</option>
            ))}
          </select>
          <select
            className="w-20 p-2 border rounded"
            value={form.teamAGrade}
            onChange={(e) => updateField("teamAGrade", e.target.value)}
            required
          >
            <option value="">-</option>
            <option value="A">A</option>
            <option value="B">B</option>
            <option value="C">C</option>
            <option value="D">D</option>
            <option value="E">E</option>
            <option value="F">F</option>
            <option value="G">G</option>
            <option value="H">H</option>
            <option value="I">I</option>
            <option value="J">J</option>
            <option value="K">K</option>
            <option value="L">L</option>
          </select>
        </div>

        {/* Team B */}
        <div className="flex gap-2">
          <select
            className="flex-1 p-2 border rounded"
            value={form.teamB}
            onChange={(e) => updateField("teamB", e.target.value)}
            required
          >
            <option value="">Select Team B</option>
            {teams.map(team => (
              <option key={team.id} value={team.name}>{team.name}</option>
            ))}
          </select>
          <select
            className="w-20 p-2 border rounded"
            value={form.teamBGrade}
            onChange={(e) => updateField("teamBGrade", e.target.value)}
            required
          >
            <option value="">-</option>
            <option value="A">A</option>
            <option value="B">B</option>
            <option value="C">C</option>
            <option value="D">D</option>
            <option value="E">E</option>
            <option value="F">F</option>
            <option value="G">G</option>
            <option value="H">H</option>
            <option value="I">I</option>
            <option value="J">J</option>
            <option value="K">K</option>
            <option value="L">L</option>
          </select>
        </div>

        {/* Age Group */}
        <select
          className="w-full p-2 border rounded"
          value={form.ageGroup}
          onChange={(e) => updateField("ageGroup", e.target.value)}
          required
        >
          <option value="">Select Age Group</option>
          <option value="u7">u7</option>
          <option value="u8">u8</option>
          <option value="u9">u9</option>
          <option value="u10">u10</option>
          <option value="u11">u11</option>
          <option value="u12">u12</option>
          <option value="u13">u13</option>
          <option value="u14">u14</option>
          <option value="u15">u15</option>
          <option value="u16">u16</option>
          <option value="u17">u17</option>
          <option value="u18">u18</option>
          <option value="u19">u19</option>
          <option value="u20">u20</option>
          <option value="u21">u21</option>
          <option value="Club">Club</option>
          <option value="Seniors">Seniors</option>
        </select>

        {/* Competition */}
        <select
          className="w-full p-2 border rounded"
          value={form.competition}
          onChange={(e) => updateField("competition", e.target.value)}
          required
        >
          <option value="">Select Competition</option>
          {competitions.map((comp) => (
            <option key={comp} value={comp}>{comp}</option>
          ))}
        </select>

        {/* Date */}
        <input
          className="w-full p-2 border rounded"
          type="date"
          value={form.date}
          onChange={(e) => updateField("date", e.target.value)}
        />

        {/* Time */}
        <input
          className="w-full p-2 border rounded"
          type="time"
          value={form.time}
          onChange={(e) => updateField("time", e.target.value)}
        />

        {/* Venue */}
        <select
          className="w-full p-2 border rounded"
          value={form.venue}
          onChange={(e) => updateField("venue", e.target.value)}
          required
        >
          <option value="">Select Venue</option>
          {venues.map((venue) => (
            <option key={venue} value={venue}>{venue}</option>
          ))}
        </select>

        {/* Field */}
        <select
          className="w-full p-2 border rounded"
          value={form.field}
          onChange={(e) => updateField("field", e.target.value)}
          required
          disabled={!form.venue}
        >
          <option value="">Select Field</option>
          {fieldsForVenue.map((field) => (
            <option key={field} value={field}>{field}</option>
          ))}
        </select>

        {/* Checkboxes */}
        <div className="mt-3">
          <h3 className="font-semibold mb-2">Required Match Officials:</h3>
          
          <div className="grid grid-cols-2 gap-3">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={form.needsReferee}
                onChange={(e) => updateField("needsReferee", e.target.checked)}
              />
              <span>Referee</span>
            </label>

            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={form.needsAssistantReferee}
                onChange={(e) =>
                  updateField("needsAssistantReferee", e.target.checked)
                }
              />
              <span>Assistant Referee</span>
            </label>

            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={form.needsAssistant4}
                onChange={(e) => updateField("needsAssistant4", e.target.checked)}
              />
              <span>Assistant 4</span>
            </label>

            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={form.needsAssistant5}
                onChange={(e) => updateField("needsAssistant5", e.target.checked)}
              />
              <span>Assistant 5</span>
            </label>

            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={form.needsTimeKeeper}
                onChange={(e) => updateField("needsTimeKeeper", e.target.checked)}
              />
              <span>Time Keeper</span>
            </label>

            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={form.needsPlayerMovementRecorder}
                onChange={(e) => updateField("needsPlayerMovementRecorder", e.target.checked)}
              />
              <span>Player Movement Recorder</span>
            </label>

            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={form.needsStatistician}
                onChange={(e) => updateField("needsStatistician", e.target.checked)}
              />
              <span>Statistician</span>
            </label>

            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={form.needsTechnicalZoneManager}
                onChange={(e) => updateField("needsTechnicalZoneManager", e.target.checked)}
              />
              <span>Technical Zone Manager</span>
            </label>

            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={form.needsPointOfOrigin1}
                onChange={(e) => updateField("needsPointOfOrigin1", e.target.checked)}
              />
              <span>Point of Origin 1</span>
            </label>

            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={form.needsPointOfOrigin2}
                onChange={(e) => updateField("needsPointOfOrigin2", e.target.checked)}
              />
              <span>Point of Origin 2</span>
            </label>
          </div>
        </div>

        <button className="w-full bg-green-600 text-white p-2 rounded">
          Submit Request
        </button>
      </form>
    </div>
  );
}
